/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
    ClipboardList, CheckCircle2, Clock, AlertTriangle, Filter, Search,
    CheckSquare, ArrowRight, User, Calendar, MoreHorizontal, LayoutGrid, List,
    Plus, Loader2
} from 'lucide-react';
import { fetchAllTasks, fetchCaseTasks, updateTask } from '../api/tasks';
import { motion, AnimatePresence } from 'framer-motion';
import { useCelebration, CelebrationOverlay } from './CelebrationEffects';

const TasksDashboard = ({ onNavigateToCase, currentUserEmail, collabCases = [] }) => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);

    // Client-side pagination
    const [visibleCount, setVisibleCount] = useState(50);

    const [viewMode, setViewMode] = useState('list'); // 'list' | 'board'
    // Filters: 'all' | 'mine' | 'pending' | 'completed' | 'overdue' | 'high_priority'
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');

    // Celebration Hook
    const { triggerCelebration, celebrationState } = useCelebration();

    useEffect(() => {
        loadTasks();
    }, [collabCases]);

    const loadTasks = async () => {
        setLoading(true);
        console.log("[TasksDashboard] Loading tasks, collabCases:", collabCases?.length || 0);
        try {
            // Fetch tasks from owned cases
            const ownedTasks = await fetchAllTasks();
            let allTasks = Array.isArray(ownedTasks) ? ownedTasks : [];
            console.log("[TasksDashboard] Owned tasks:", allTasks.length);

            // Fetch tasks from collab/shared cases
            if (collabCases && collabCases.length > 0) {
                console.log("[TasksDashboard] Fetching tasks from", collabCases.length, "collab cases");
                const collabTaskPromises = collabCases.map(async (collabCase) => {
                    const caseId = collabCase.id || collabCase.caseId || collabCase.case_id;
                    console.log("[TasksDashboard] Collab case:", caseId, collabCase);
                    if (!caseId) return [];
                    try {
                        const caseTasks = await fetchCaseTasks(caseId);
                        console.log("[TasksDashboard] Tasks for collab case", caseId, ":", caseTasks);
                        // Add case context to each task if not already present
                        const tasksArray = Array.isArray(caseTasks) ? caseTasks : (caseTasks?.tasks || caseTasks?.data || []);
                        return tasksArray.map(task => ({
                            ...task,
                            caseId: task.caseId || caseId,
                            caseName: task.caseName || collabCase.borrowerName || collabCase.name || `Case ${collabCase.caseNumber}`,
                            isSharedCase: true
                        }));
                    } catch (err) {
                        console.warn(`[TasksDashboard] Failed to fetch tasks for collab case ${caseId}`, err);
                        return [];
                    }
                });

                const collabTasksResults = await Promise.all(collabTaskPromises);
                const collabTasks = collabTasksResults.flat();
                console.log("[TasksDashboard] Collab tasks fetched:", collabTasks.length);

                // Merge and dedupe (by task id)
                const existingIds = new Set(allTasks.map(t => t.id));
                const uniqueCollabTasks = collabTasks.filter(t => !existingIds.has(t.id));
                console.log("[TasksDashboard] Unique collab tasks after dedupe:", uniqueCollabTasks.length);
                allTasks = [...allTasks, ...uniqueCollabTasks];
            }

            console.log("[TasksDashboard] Total tasks:", allTasks.length);
            setTasks(allTasks);
        } catch (err) {
            console.error("[TasksDashboard] Failed to load tasks", err);
            setTasks([]);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusToggle = async (task) => {
        const isCompleting = task.status !== 'completed';
        const newStatus = isCompleting ? 'completed' : 'pending';

        // Trigger Asana-Style Celebration
        if (isCompleting) {
            triggerCelebration();
        }

        // Optimistic update
        setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));

        try {
            await updateTask(task.caseId, task.id, { status: newStatus });
        } catch (err) {
            console.error("Failed to update status", err);
            setTasks(prev => prev.map(t => t.id === task.id ? task : t));
        }
    };

    const getFilteredTasks = () => {
        return tasks.filter(task => {
            // 1. Search Filter
            if (search) {
                const term = search.toLowerCase();
                if (!task.title.toLowerCase().includes(term) && !task.caseName?.toLowerCase().includes(term)) return false;
            }

            // 2. Category Filter
            switch (filter) {
                case 'mine':
                    return task.assigneeEmail === currentUserEmail || task.assignee === 'Me' || (task.assignee && task.assignee.includes('Sarah'));

                case 'pending':
                    return task.status !== 'completed';

                case 'completed':
                    return task.status === 'completed';

                case 'overdue':
                    return new Date(task.dueDate) < new Date() && task.status !== 'completed';

                case 'high_priority':
                    return task.priority === 'high' || task.priority === 'critical';

                case 'all':
                default:
                    return true;
            }
        });
    };

    const filteredTasks = getFilteredTasks();
    const displayedTasks = filteredTasks.slice(0, visibleCount);
    const hasMore = visibleCount < filteredTasks.length;

    // Stats - Calculated from ALL tasks (Global view)
    const total = tasks.length;
    const completed = tasks.filter(t => t.status === 'completed').length;
    const pending = total - completed;
    const overdue = tasks.filter(t => new Date(t.dueDate) < new Date() && t.status !== 'completed').length;
    const highPriority = tasks.filter(t => t.priority === 'high' || t.priority === 'critical').length;

    const priorityColor = (p) => {
        switch (p) {
            case 'critical': return 'text-red-400 bg-red-400/10 border-red-400/20';
            case 'high': return 'text-orange-400 bg-orange-400/10 border-orange-400/20';
            case 'medium': return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
            default: return 'text-slate-400 dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-200';
        }
    };

    return (
        <div className="h-full dark:bg-slate-950 bg-[#f7f4ee] flex flex-col overflow-hidden relative">
            <CelebrationOverlay state={celebrationState} />

            {/* Hero */}
            <div className="relative overflow-hidden px-6 py-10 sm:py-12">
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(120deg, rgba(13, 148, 136, 0.98), rgba(15, 23, 42, 0.95))'
                    }}
                />
                <div
                    className="absolute inset-0 opacity-70"
                    style={{
                        backgroundImage: 'radial-gradient(circle at 15% 15%, rgba(255,255,255,0.3), transparent 40%), radial-gradient(circle at 80% 10%, rgba(255,255,255,0.2), transparent 35%)'
                    }}
                />
                <div className="relative max-w-5xl mx-auto">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                        <div className="text-white">
                            <p className="text-sm uppercase tracking-[0.2em] text-white/70">Tasks workspace</p>
                            <h2 className="text-3xl sm:text-4xl font-semibold mt-2">Keep every case on track</h2>
                            <p className="text-sm sm:text-base text-white/80 mt-2 max-w-xl">
                                Track priorities, deadlines, and ownership across your entire pipeline.
                            </p>
                        </div>
                        <div className="w-full lg:w-[360px]">
                            <label className="text-xs font-semibold text-white/80 uppercase tracking-[0.2em]">
                                Search tasks
                            </label>
                            <div className="relative mt-3">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search all tasks..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 bg-white/95 dark:bg-slate-800 border-0 rounded-xl shadow-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-teal-200"
                                />
                            </div>
                            <div className="flex flex-wrap gap-2 mt-4 text-xs text-white/80">
                                <span className="px-3 py-1.5 rounded-full bg-white/15 text-xs font-medium">
                                    {total} tasks
                                </span>
                                <span className="px-3 py-1.5 rounded-full bg-white/15 text-xs font-medium">
                                    {pending} pending
                                </span>
                            </div>
                            <button
                                onClick={() => setFilter('all')}
                                className="mt-4 w-full px-4 py-2.5 bg-white text-slate-900 hover:bg-white/90 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                            >
                                <ClipboardList className="w-5 h-5" />
                                View All Tasks
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Header Actions */}
            <div className="px-6 pb-6 -mt-8">
                <div className="px-6 py-6 border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/80 backdrop-blur rounded-2xl shadow-xl flex flex-col gap-6">

                {/* Stats Cards - Interactive Filters */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        { label: 'Pending', count: pending, icon: Clock, color: 'blue', filterKey: 'pending' },
                        { label: 'Overdue', count: overdue, icon: AlertTriangle, color: 'red', filterKey: 'overdue' },
                        { label: 'High Priority', count: highPriority, icon: AlertTriangle, color: 'orange', filterKey: 'high_priority' },
                        { label: 'Completed', count: completed, icon: CheckCircle2, color: 'emerald', filterKey: 'completed' }
                    ].map((stat, i) => {
                        const isActive = filter === stat.filterKey;
                        return (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                                key={stat.label}
                                onClick={() => setFilter(isActive ? 'all' : stat.filterKey)}
                            className={`p-4 rounded-xl flex items-center justify-between border cursor-pointer transition-all group relative overflow-hidden ${isActive
                                    ? `bg-${stat.color}-900/20 border-${stat.color}-500/50 ring-1 ring-${stat.color}-500/50`
                                    : 'dark:bg-slate-900/50 bg-white border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                                    }`}
                            >
                                <div className="z-10">
                                    <p className={`text-xs font-bold uppercase tracking-wider ${isActive ? `text-${stat.color}-300` : 'text-slate-500'}`}>{stat.label}</p>
                                    <h3 className={`text-2xl font-bold mt-1 group-hover:scale-105 transition-transform origin-left ${isActive ? 'text-white' : `text-${stat.color}-400 dark:text-${stat.color}-400`}`}>
                                        {loading ? '-' : stat.count}
                                    </h3>
                                </div>
                                <div className={`p-3 rounded-xl transition-colors z-10 ${isActive
                                    ? `bg-${stat.color}-500 text-white shadow-lg shadow-${stat.color}-500/20`
                                    : `bg-${stat.color}-500/10 text-${stat.color}-400 group-hover:bg-${stat.color}-500/20`
                                    }`}>
                                    <stat.icon size={20} />
                                </div>
                                {/* Active Glow Background */}
                                {isActive && (
                                    <div className={`absolute inset-0 bg-gradient-to-br from-${stat.color}-500/10 to-transparent pointer-events-none`} />
                                )}
                            </motion.div>
                        );
                    })}
                </div>

                {/* Secondary Filters */}
                <div className="flex items-center gap-4">
                    <div className="relative flex-1 max-w-md group">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-teal-400 transition-colors" size={16} />
                        <input
                            type="text"
                            placeholder="Search tasks, cases, or assignees..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full dark:bg-slate-900 bg-white border dark:border-slate-700 border-slate-300 rounded-lg pl-10 pr-4 py-2 dark:text-slate-200 text-slate-800 dark:placeholder-slate-500 placeholder-slate-400 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
                        />
                    </div>
                    {/* Filter Pills - Updated to remove redundant ones or keep 'My Tasks' */}
                    <div className="flex items-center gap-1 border-l border-slate-700 pl-4">
                        <button
                            onClick={() => setFilter('all')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all capitalize ${filter === 'all'
                                ? 'bg-slate-700 text-white'
                                : 'text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 dark:hover:text-slate-200 hover:text-slate-700'
                                }`}
                        >
                            All
                        </button>
                        <button
                            onClick={() => setFilter('mine')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all capitalize ${filter === 'mine'
                                ? 'bg-teal-500/10 text-teal-400 border border-teal-500/20'
                                : 'text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 dark:hover:text-slate-200 hover:text-slate-700 border border-transparent'
                                }`}
                        >
                            My Tasks
                        </button>
                    </div>
                    {/* Showing Active Filter Label if applied via cards */}
                    {filter !== 'all' && filter !== 'mine' && (
                        <div className="text-xs text-slate-500 flex items-center gap-2">
                            Filtering by: <span className="dark:text-white text-slate-800 font-semibold uppercase">{filter.replace('_', ' ')}</span>
                            <button onClick={() => setFilter('all')} className="hover:text-red-400">×</button>
                        </div>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                        <span className="hidden sm:inline text-xs uppercase tracking-[0.2em] text-slate-400">View</span>
                        <div className="flex dark:bg-slate-800 bg-slate-100 p-1 rounded-lg border dark:border-slate-700 border-slate-200">
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded-md transition-all duration-200 ${viewMode === 'list' ? 'dark:bg-slate-600 bg-white dark:text-white text-teal-600 shadow-md' : 'text-slate-400 hover:text-teal-600 dark:hover:text-white hover:bg-white dark:hover:bg-slate-700/50'}`}
                                title="List View"
                            >
                                <List size={18} />
                            </button>
                            <button
                                onClick={() => setViewMode('board')}
                                className={`p-2 rounded-md transition-all duration-200 ${viewMode === 'board' ? 'dark:bg-slate-600 bg-white dark:text-white text-teal-600 shadow-md' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700/50'}`}
                                title="Board View"
                            >
                                <LayoutGrid size={18} />
                            </button>
                        </div>
                    </div>
                </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden p-6 dark:bg-slate-950 bg-slate-50 flex flex-col">
                {loading ? (
                    <div className="space-y-4 animate-pulse max-w-4xl mx-auto mt-8 flex-1 text-center">
                        <div className="flex items-center justify-center gap-3 text-slate-400 mb-4">
                            <Loader2 className="animate-spin" />
                            <span>Loading execution tasks from all cases...</span>
                        </div>
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="h-16 dark:bg-slate-900 bg-white rounded-xl w-full border dark:border-slate-800 border-slate-200" />
                        ))}
                    </div>
                ) : filteredTasks.length === 0 ? (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center h-full text-slate-500 flex-1">
                        <div className="w-24 h-24 dark:bg-slate-900 bg-slate-200 rounded-full flex items-center justify-center mb-6">
                            <CheckSquare size={40} className="opacity-50" />
                        </div>
                        <h3 className="text-lg font-medium dark:text-white text-slate-900">No tasks found</h3>
                        <p>Adjust your filters or query to find what you're looking for.</p>
                        <button onClick={() => setFilter('all')} className="mt-4 text-sky-400 hover:text-sky-300 text-sm">
                            Clear all filters
                        </button>
                    </motion.div>
                ) : (
                    <div className="flex-1 overflow-y-auto min-h-0 custom-scrollbar pb-6">
                        {viewMode === 'list' && (
                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl overflow-hidden shadow-xl max-w-6xl mx-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead className="dark:bg-slate-950 bg-slate-50 border-b dark:border-slate-800 border-slate-200">
                                        <tr>
                                            <th className="px-6 py-4 text-xs font-bold dark:text-slate-400 text-slate-500 uppercase tracking-wider w-12">Status</th>
                                            <th className="px-6 py-4 text-xs font-bold dark:text-slate-400 text-slate-500 uppercase tracking-wider">Task Details</th>
                                            <th className="px-6 py-4 text-xs font-bold dark:text-slate-400 text-slate-500 uppercase tracking-wider">Case Context</th>
                                            <th className="px-6 py-4 text-xs font-bold dark:text-slate-400 text-slate-500 uppercase tracking-wider">Priority</th>
                                            <th className="px-6 py-4 text-xs font-bold dark:text-slate-400 text-slate-500 uppercase tracking-wider">Due Date</th>
                                            <th className="px-6 py-4 text-xs font-bold dark:text-slate-400 text-slate-500 uppercase tracking-wider">Assignee</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y dark:divide-slate-800 divide-slate-100">
                                        {displayedTasks.map((task, i) => (
                                            <motion.tr
                                                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.02 }}
                                                key={task.id}
                                                className="hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors group"
                                            >
                                                <td className="px-6 py-4">
                                                    <motion.button
                                                        whileHover={{ scale: 1.1 }}
                                                        whileTap={{ scale: 0.9 }}
                                                        onClick={() => handleStatusToggle(task)}
                                                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${task.status === 'completed'
                                                            ? 'bg-emerald-500 border-emerald-500 scale-110 shadow-lg shadow-emerald-500/20'
                                                            : 'border-slate-500 hover:border-sky-500'
                                                            }`}
                                                    >
                                                        <CheckCircle2 size={14} className={`text-white transition-opacity ${task.status === 'completed' ? 'opacity-100' : 'opacity-0'}`} />
                                                    </motion.button>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`text-sm font-medium transition-all ${task.status === 'completed' ? 'text-slate-500 line-through' : 'dark:text-slate-200 text-slate-800'}`}>
                                                        {task.title}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {task.caseId ? (
                                                        <button
                                                            onClick={() => onNavigateToCase && onNavigateToCase(task.caseId)}
                                                            className="text-xs px-2.5 py-1 rounded-full dark:bg-slate-800 bg-white hover:bg-sky-50 dark:hover:bg-sky-500/20 text-sky-600 dark:text-sky-400 hover:text-sky-500 dark:hover:text-sky-300 border dark:border-slate-700 border-slate-200 hover:border-sky-300 dark:hover:border-sky-500/30 transition-all flex items-center w-fit gap-1 group/btn"
                                                        >
                                                            {task.caseName}
                                                            <ArrowRight size={10} className="opacity-0 -translate-x-1 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 transition-all" />
                                                        </button>
                                                    ) : <span className="text-slate-400 dark:text-slate-600">-</span>}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${priorityColor(task.priority)}`}>
                                                        {task.priority || 'Low'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className={`flex items-center gap-2 text-sm ${new Date(task.dueDate) < new Date() && task.status !== 'completed' ? 'text-red-500 dark:text-red-400 font-medium' : 'text-slate-500 dark:text-slate-400'
                                                        }`}>
                                                        {task.dueDate ? new Date(task.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2" title={task.assignee}>
                                                        <div className={`w-7 h-7 rounded-full ${task.assigneeColor || 'bg-slate-500 dark:bg-slate-600'} flex items-center justify-center text-xs text-white font-medium ring-2 dark:ring-slate-900 ring-white`}>
                                                            {(task.assignee || 'U').charAt(0)}
                                                        </div>
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {viewMode === 'board' && (
                            <div className="flex gap-6 h-full overflow-x-auto pb-4 items-start px-2">
                                {['Pending', 'Completed'].map(column => {
                                    const statusKey = column.toLowerCase();
                                    // When in BOARD view, if filtering by status, showing empty columns might be weird.
                                    // But let's keep it consistent. If filtering "Completed", Pending col will be empty.
                                    const columnTasks = filteredTasks.filter(t => statusKey === 'pending' ? t.status !== 'completed' : t.status === 'completed');
                                    const displayedBoardTasks = columnTasks.slice(0, 50);

                                    return (
                                        <div key={column} className="w-80 flex-shrink-0 flex flex-col h-full dark:bg-slate-900/30 bg-slate-100/50 border dark:border-slate-800/50 border-slate-200/50 rounded-xl">
                                            <div className="p-4 border-b dark:border-slate-800/50 border-slate-200 flex justify-between items-center sticky top-0 dark:bg-slate-900/90 bg-white/90 backdrop-blur-md rounded-t-xl z-10">
                                                <h3 className="font-bold dark:text-slate-200 text-slate-700">{column}</h3>
                                                <span className="text-xs dark:bg-slate-800 bg-slate-200 px-2 py-1 rounded dark:text-slate-400 text-slate-600">{columnTasks.length}</span>
                                            </div>
                                            <div className="p-3 space-y-3 overflow-y-auto min-h-0 custom-scrollbar">
                                                {displayedBoardTasks.map(task => (
                                                    <motion.div
                                                        layoutId={task.id}
                                                        key={task.id}
                                                        className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 p-4 rounded-lg shadow-sm hover:ring-1 hover:ring-teal-500/50 transition-all cursor-pointer group"
                                                    >
                                                        <div className="flex justify-between items-start mb-2">
                                                            <span className={`text-xs px-1.5 py-0.5 rounded border ${priorityColor(task.priority)}`}>{task.priority}</span>
                                                            <motion.button
                                                                whileHover={{ scale: 1.1 }}
                                                                whileTap={{ scale: 0.9 }}
                                                                onClick={() => handleStatusToggle(task)}
                                                                className="text-slate-500 hover:text-emerald-400"
                                                            >
                                                                {task.status === 'completed' ? <CheckCircle2 size={20} className="text-emerald-500 drop-shadow-md" /> : <div className="w-4 h-4 border border-slate-500 rounded-full" />}
                                                            </motion.button>
                                                        </div>
                                                        <h4 className={`font-medium text-sm mb-1 ${task.status === 'completed' ? 'text-slate-500' : 'dark:text-slate-200 text-slate-800'}`}>{task.title}</h4>
                                                        <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                                                            <span className="flex items-center gap-1 truncate max-w-[100px]">{task.caseName}</span>
                                                            <div className={`w-6 h-6 rounded-full ${task.assigneeColor} flex items-center justify-center text-white text-[10px]`}>
                                                                {(task.assignee || 'U').charAt(0)}
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                ))}
                                                {columnTasks.length > 50 && (
                                                    <div className="text-center text-xs text-slate-500 py-2">
                                                        + {columnTasks.length - 50} more
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Load More Button (Client-side) */}
                        {hasMore && viewMode === 'list' && (
                            <div className="flex justify-center mt-6 mb-8">
                                <button
                                    onClick={() => setVisibleCount(c => c + 50)}
                                    className="px-6 py-2 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 dark:text-slate-300 text-slate-600 font-medium rounded-lg transition-all"
                                >
                                    Show More ({filteredTasks.length - visibleCount} remaining)
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TasksDashboard;
